import { useCallback, useEffect, useState } from 'react'
import Chart from 'react-apexcharts'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import { getISOCurrencyMain } from '../../../../utils/reducers/getters'
import { roundNumberByParams, preciseDecimalSum } from '@teinor/erp/prices'
import { getCurrencySymbol } from '../../../../utils/globals/price'
import CustomSelect from '../../../../forms/CustomSelect'
import { TwinDictionary } from '../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import TwinIcon, { TwinIconProp } from '../../../TwinIcon'
import { faArrowTrendDown, faArrowTrendUp } from '@fortawesome/pro-light-svg-icons'
import { SellTicketTotalAmountData } from './types'
import { displayFormatedNumber } from '../../../../utils/globals/numbers'
import './sellticketamount.sass'

interface DSBSellTicketAmountProps extends DSBSellTicketAmountLogicProps {
    title?: string
    icon?: TwinIconProp
    color: string
}
const DSBSellTicketAmount = ({ title, sellTicketStatus, dates, icon, color }: DSBSellTicketAmountProps) => {
    const { t } = useTwinTranslation()
    const mainCurrency = getISOCurrencyMain()
    const currencySymbol = getCurrencySymbol(mainCurrency)
    const { sellTicketTotal, onChangeDateSelect, selectedDate } = useDSBSellTicketAmountLogic({ sellTicketStatus, dates })
    const items: TwinDictionary = {
        week: { name: t('weekly', 'Semanal') },
        month: { name: t('monthly', 'Mensual') },
        year: { name: t('yearly', 'Anual') }
    }
    const dataSeries: number[] = []
    for (const key in sellTicketTotal.myResultByDates) {
        const total = sellTicketTotal.myResultByDates[key]
        dataSeries.push(total)
    }
    if (!sellTicketTotal.totalCurrent) {
        dataSeries.push(0)
    }
    if (!sellTicketTotal.totalPrevious) {
        dataSeries.unshift(0)
    }
    const series = [
        {
            name: t('total', 'Total'),
            data: dataSeries
        }
    ]
    return (
        <div className='flex flex-col flex-auto h-1 sell_ticket_amount'>
            <div className='flex relative gap-x-10'>
                {icon ? <div className='sell_ticket_amount_icon flex items-center text-green-43 rounded-full justify-center mr-10'><TwinIcon icon={icon} fontSize={24}></TwinIcon></div> : null}
                <div>
                    <h3 className='twin_elipsis'>{title}</h3>
                    <div className='regular14 text-gray-8F'>{displayFormatedNumber(sellTicketTotal.moneyTotal) + ' ' + currencySymbol} </div>
                </div>
                <CustomSelect className='sell_ticket_amount_selector mt-5 z-1' value={selectedDate} items={items} label={''} onChange={onChangeDateSelect} maxItems={3} />
            </div>
            <div className='mt-auto flex items-center'>
                <div className='flex items-center w-full'>
                    <div className='mr-auto'>
                        <span className='regular26'>{displayFormatedNumber(sellTicketTotal.finalAmountPercentage)}%</span>
                        <TwinIcon className={(sellTicketTotal.isLoss ? 'text-red-BA' : 'text-green-43') + ' ml-10'} icon={sellTicketTotal.isLoss ? faArrowTrendDown : faArrowTrendUp} fontSize={20} />
                    </div>
                    <div className='absolute right-5 top-7 h-90'>
                        <Chart options={{
                            chart: {
                                type: 'line',
                                events: {
                                    mounted: (chart) => {
                                        chart.windowResizeHandler()
                                    }
                                },
                                toolbar: {
                                    show: false
                                },
                                zoom: {
                                    enabled: false
                                },
                            },
                            grid: {
                                xaxis: {
                                    lines: {
                                        show: false
                                    }
                                },
                                yaxis: {
                                    lines: {
                                        show: false
                                    }
                                }
                            },
                            yaxis: {
                                show: false,
                                labels: {
                                    show: false,
                                },
                            },
                            xaxis: {
                                labels: {
                                    show: false
                                },
                                axisBorder: {
                                    show: false
                                },
                                axisTicks: {
                                    show: false
                                },
                                tooltip: {
                                    enabled: false
                                }
                            },
                            tooltip: {
                                enabled: false
                            },
                            stroke: {
                                curve: 'smooth',
                                width: 3
                            },
                            dataLabels: {
                                enabled: false
                            },
                            colors: [color],
                        }} series={series} type="line" width={120} height={90} />
                    </div>
                </div>
            </div>
        </div>
    )
}

interface DSBSellTicketAmountLogicProps {
    sellTicketStatus: number[]
    dates: TwinDictionary
}

const useDSBSellTicketAmountLogic = ({ sellTicketStatus, dates }: DSBSellTicketAmountLogicProps) => {
    const [sellTicketTotal, setSellTicketTotal] = useState<SellTicketTotalAmountData>({ moneyTotal: 0, finalAmountPercentage: 0, isLoss: false, totalPrevious: 0, totalCurrent: 0, myResultByDates: {} })
    const [selectedDate, setSelectedDate] = useState<string>('month')
    const getData = useCallback(async (option = 'month') => {
        const myStartDate = dates[option + 'Start']
        const myEndDate = dates.currentDate
        const myStartDatePrevious = dates[option + 'PreviousStart']
        const myEndDatePrevious = dates[option + 'PreviousEnd']

        const resultPrevious = await twinFetchPostJSON('/api/app/order/tpv/sellTicket/getAllSellTicketsForStats', {
            attributes: ['total_without_vat', 'createdAt'],
            where: {
                'SellTicket.createdAt': { '$betweenN': [myStartDatePrevious, myEndDatePrevious] },
                status: { '$or': sellTicketStatus }
            },
            order: [['createdAt', 'ASC']]
        })
        const resultCurrent = await twinFetchPostJSON('/api/app/order/tpv/sellTicket/getAllSellTicketsForStats', {
            attributes: ['total_without_vat', 'createdAt'],
            where: {
                'SellTicket.createdAt': { '$betweenN': [myStartDate, myEndDate] },
                status: { '$or': sellTicketStatus },
                is_last: true,
            },
            order: [['createdAt', 'ASC']]
        })
        let finalAmountPercentage = 0
        let totalCurrent: number = 0
        let totalPrevious: number = 0
        if (resultPrevious && resultCurrent) {
            let myResultByDates: TwinDictionary = {}
            for (const element of resultPrevious) {
                const myAmount = parseFloat(element.total_without_vat)
                totalPrevious = parseFloat(roundNumberByParams(preciseDecimalSum(totalPrevious, myAmount)).toFixed(2))
                const myDay = option === 'year' ? (new Date(element.createdAt).getFullYear() + '-' + new Date(element.createdAt).getMonth()) : new Date(element.createdAt).toDateString()
                if (!myResultByDates[myDay]) {
                    myResultByDates[myDay] = 0
                }
                myResultByDates[myDay] = (myResultByDates?.[myDay] || 0) + myAmount
            }

            for (const element of resultCurrent) {
                const myAmount = parseFloat(element.total_without_vat)
                totalCurrent = parseFloat(roundNumberByParams(preciseDecimalSum(totalCurrent, myAmount)).toFixed(2))
                const myDay = option === 'year' ? (new Date(element.createdAt).getFullYear() + '-' + new Date(element.createdAt).getMonth()) : new Date(element.createdAt).toDateString()
                if (!myResultByDates[myDay]) {
                    myResultByDates[myDay] = 0
                }
                myResultByDates[myDay] = (myResultByDates?.[myDay] || 0) + myAmount
            }
            if (totalPrevious === 0) {
                if (totalCurrent > 0) {
                    finalAmountPercentage = 100
                } else if (totalCurrent === 0) {
                    finalAmountPercentage = 0
                }
            } else {
                finalAmountPercentage = parseFloat((((totalCurrent * 100) / totalPrevious) - 100).toFixed(2))
            }
            setSellTicketTotal({ moneyTotal: totalCurrent, isLoss: totalPrevious > totalCurrent ? true : false, finalAmountPercentage, totalPrevious, totalCurrent, myResultByDates })
        }
    }, [setSellTicketTotal, sellTicketStatus, dates])

    const onChangeDateSelect = useCallback((value: any) => {
        setSelectedDate(value)
        getData(value)
    }, [setSelectedDate, getData])

    useEffect(() => {
        getData()
    }, [getData])
    return { sellTicketTotal, onChangeDateSelect, selectedDate }
}

export default DSBSellTicketAmount